/**
 * Composant de Validation des Virements et Generation des Recus ESS
 * Orchestrateur principal - design ultra-moderne
 */
import RecusMandatsSEPA from '../sepa/RecusMandatsSEPA';
import RejetsMandatsSEPA from '../sepa/RejetsMandatsSEPA';
import TauxEncaissement from '../sepa/TauxEncaissement';
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import { Badge } from '../ui/badge';

import { FileText, Loader2, CheckCheck, Trash2, Square, CheckSquare } from 'lucide-react';
import GenerationRecuManuel from '../GenerationRecuManuel';

import { API, TYPE_CONFIG } from './constants';
import DashboardTab from './DashboardTab';
import PartielsTab from './PartielsTab';
import SepaCbPanel from '../paiementsCb/SepaCbPanel';
import { PaiementsDomiumESS } from '../paiementsCb/PaiementsDomiumESS';
import ArchivesTab from './ArchivesTab';
import ComptabiliteTab from './ComptabiliteTab';
import AcomptesTab from './AcomptesTab';
import DocumentCard from './DocumentCard';
import Modals from './Modals';
import HeaderSection from './HeaderSection';
import { useArchiveLogic } from './useArchiveLogic';
import { usePrintExport } from './usePrintExport';
import { useStripeActions } from './useStripeActions';
import { useSelectionLogic } from './useSelectionLogic';
import useValidationData from './useValidationData';
import useValidationCompta from './useValidationCompta';
import useValidationActions from './useValidationActions';
import PaiementsAReclamer from './PaiementsAReclamer';
import TableauImpayes from './TableauImpayes';

export default function ValidationVirementsESS() {
  const data = useValidationData();
  const {
    _acomptesData, _comptaStats, _recusESS, _selectedAcompte, _selectedPaiement, _setAcomptesData,
    _setComptaStats, _setShowAcomptesTab, _showAcomptesTab, _showRecuEcheanceModal, _showStripeCBModal, acompteForm,
    activeTab, allDocuments, archiving, assignForm, bulkProcessing, calculateMontantHT,
    calculateMontantTTC, coopers, creatingEcheancier, dashboardStats, documentsEnAttente, documentsValides,
    echeanceForm, echeancierForm, experts, exportLoading, fetchData, filterDateDebut,
    filterDateFin, filterMontantMax, filterMontantMin, filterRecuStatus, filterType, filteredDocuments,
    generatingRecu, getAuthHeader, loading, loadingPartialPayments, members, partialPaymentData,
    partialPaymentsHistory, processingPartialPayment, refreshing, searchTerm, selectedDocument, selectedIds,
    selectedPaiementIndex, sepaModalEmail, sepaModalName, setAcompteForm, setActiveTab, setAllDocuments,
    setArchiving, setAssignForm, setBulkProcessing, setCoopers, setCreatingEcheancier, setEcheanceForm,
    setEcheancierForm, setExperts, setExportLoading, setFilterDateDebut, setFilterDateFin, setFilterMontantMax,
    setFilterMontantMin, setFilterRecuStatus, setFilterType, setGeneratingRecu, setLoading, setLoadingPartialPayments,
    setMembers, setPartialPaymentData, setPartialPaymentsHistory, setProcessingPartialPayment, setRecusESS, setRefreshing,
    setSearchTerm, setSelectedAcompte, setSelectedDocument, setSelectedIds, setSelectedPaiement, setSelectedPaiementIndex,
    setSepaModalEmail, setSepaModalName, setShareForm, setSharing, setShowAdvancedFilters, setShowAssignModal,
    setShowEcheanceModal, setShowEcheancierModal, setShowEditAcompteModal, setShowPartialPaymentModal, setShowRecuEcheanceModal, setShowShareModal,
    setShowStripeCBModal, setShowStripeSepaModal, setShowValidationModal, setStripeProcessing, setStripeSepaType, setUpdatingEcheance,
    setValidating, setValidationData, shareForm, sharing, showAdvancedFilters, showAssignModal,
    showEcheanceModal, showEcheancierModal, showEditAcompteModal, showPartialPaymentModal, showShareModal, showStripeSepaModal,
    showValidationModal, stats, stripeProcessing, stripeSepaType, updatingEcheance, validating,
    validationData,
  } = data;

  const { getAllAcomptesESS, calculatedComptaStats } = useValidationCompta({ allDocuments, calculateMontantTTC });

  const {
    _normalizeMode, addEcheanceToForm, cancelAcompte, handleCreateEcheancier, handlePartialPayment, handleUpdateEcheance,
    handleValidatePayment, loadPartialPaymentsHistory, openEcheanceModal, openEcheancierModal, openEditAcompteModal, openPartialPaymentModal,
    openValidationModal, removeEcheanceFromForm, resetFilters, saveAcompteModification,
  } = useValidationActions(data);

  const {
    archiveRecuToGED, archiveAllRecusToGED, downloadPartialReceipt, downloadRecu,
    openRecuEcheanceModal, generateRecuEcheance, generateRecuContributionAcquittee, viewRecu
  } = useArchiveLogic({
    API, getAuthHeader, fetchData, setArchiving, setGeneratingRecu,
    setSelectedDocument, setSelectedPaiement, setSelectedPaiementIndex, setShowRecuEcheanceModal,
    calculateMontantTTC
  });

  const {
    printEcheancier, handleDeleteEcheancier, handleResetEcheancier,
    exportAcomptes, exportEcheanciers, exportContributionsAcquittees,
    exportPaiementsParMode, exportPaiementsSEPA, exportExcelComplet,
    handleAssign, handleShare, openShareModal
  } = usePrintExport({
    API, getAuthHeader, fetchData, allDocuments, calculateMontantTTC,
    getAllAcomptesESS, calculatedComptaStats, selectedDocument, setSelectedDocument,
    filterDateDebut, filterDateFin, setGeneratingRecu, setExportLoading,
    assignForm, setAssignForm, setShowAssignModal,
    shareForm, setShareForm, setShowShareModal, setSharing
  });

  const {
    openStripeSepaModal, setupStripeSEPA: setupStripeSEPA, setupStripeSEPAWithEmail,
    scheduleSepaDebit, initiateStripeCBPayment
  } = useStripeActions({
    API, getAuthHeader, fetchData, calculateMontantTTC,
    selectedDocument, setSelectedDocument,
    setShowStripeSepaModal, setStripeSepaType, setStripeProcessing, setShowStripeCBModal,
    sepaModalEmail, setSepaModalEmail, sepaModalName, setSepaModalName
  });

  const {
    toggleSelection, selectAllFiltered, deselectAll, isAllFilteredSelected,
    handleBulkValidate, handleBulkDelete
  } = useSelectionLogic({
    API, getAuthHeader, fetchData, filteredDocuments, setBulkProcessing,
    selectedIds, setSelectedIds, documentsEnAttente, calculateMontantTTC
  });

  if (loading) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HeaderSection
        stats={stats} dashboardStats={dashboardStats} refreshing={refreshing} fetchData={fetchData}
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        filterType={filterType} setFilterType={setFilterType}
        filterMontantMin={filterMontantMin} setFilterMontantMin={setFilterMontantMin}
        filterMontantMax={filterMontantMax} setFilterMontantMax={setFilterMontantMax}
        filterDateDebut={filterDateDebut} setFilterDateDebut={setFilterDateDebut}
        filterDateFin={filterDateFin} setFilterDateFin={setFilterDateFin}
        filterRecuStatus={filterRecuStatus} setFilterRecuStatus={setFilterRecuStatus}
        showAdvancedFilters={showAdvancedFilters} setShowAdvancedFilters={setShowAdvancedFilters}
        resetFilters={resetFilters}
        activeTab={activeTab} setActiveTab={setActiveTab}
        documentsEnAttente={documentsEnAttente} documentsValides={documentsValides}
        getAllAcomptesESS={getAllAcomptesESS}
      />

      {/* === TABLEAU DE BORD === */}
      {activeTab === 'dashboard' && (
        <DashboardTab
          dashboardStats={dashboardStats}
          stats={stats}
          calculatedComptaStats={calculatedComptaStats}
          refreshing={refreshing}
          calculateMontantTTC={calculateMontantTTC}
          openValidationModal={openValidationModal}
          setActiveTab={setActiveTab}
          fetchData={fetchData}
          documentsEnAttente={documentsEnAttente}
          openPartialPaymentModal={openPartialPaymentModal}
          printEcheancier={printEcheancier}
          handleResetEcheancier={handleResetEcheancier}
          handleDeleteEcheancier={handleDeleteEcheancier}
        />
      )}

      {/* === ONGLET PAIEMENTS PARTIELS === */}
      {activeTab === 'partiels' && (
        <PartielsTab
          getAuthHeader={getAuthHeader}
          fetchData={fetchData}
          dashboardStats={dashboardStats}
          calculateMontantTTC={calculateMontantTTC}
          calculateMontantHT={calculateMontantHT}
          downloadPartialReceipt={downloadPartialReceipt}
          archiveRecuToGED={archiveRecuToGED}
          archiveAllRecusToGED={archiveAllRecusToGED}
          openRecuEcheanceModal={openRecuEcheanceModal}
          generateRecuEcheance={generateRecuEcheance}
          generateRecuContributionAcquittee={generateRecuContributionAcquittee}
          openPartialPaymentModal={openPartialPaymentModal}
          openEcheanceModal={openEcheanceModal}
          openEcheancierModal={openEcheancierModal}
          printEcheancier={printEcheancier}
          handleDeleteEcheancier={handleDeleteEcheancier}
          handleResetEcheancier={handleResetEcheancier}
          openValidationModal={openValidationModal}
          archiving={archiving}
          generatingRecu={generatingRecu}
        />
      )}

      {/* === ONGLET ARCHIVES GED === */}
      {activeTab === 'archives' && (
        <ArchivesTab
          dashboardStats={dashboardStats}
          calculateMontantTTC={calculateMontantTTC}
          downloadPartialReceipt={downloadPartialReceipt}
          downloadRecu={downloadRecu}
          setActiveTab={setActiveTab}
        />
      )}

      {/* === ONGLET COMPTABILITE === */}
      {activeTab === 'comptabilite' && (
        <ComptabiliteTab
          calculatedComptaStats={calculatedComptaStats}
          exportLoading={exportLoading}
          exportAcomptes={exportAcomptes}
          exportEcheanciers={exportEcheanciers}
          exportContributionsAcquittees={exportContributionsAcquittees}
          exportPaiementsParMode={exportPaiementsParMode}
          exportPaiementsSEPA={exportPaiementsSEPA}
          exportExcelComplet={exportExcelComplet}
          filterDateDebut={filterDateDebut}
          setFilterDateDebut={setFilterDateDebut}
          filterDateFin={filterDateFin}
          setFilterDateFin={setFilterDateFin}
          allDocuments={allDocuments}
          calculateMontantTTC={calculateMontantTTC}
        />
      )}

      {/* === ONGLET ACOMPTES === */}
      {activeTab === 'acomptes' && (
        <AcomptesTab
          getAllAcomptesESS={getAllAcomptesESS}
          generateRecuEcheance={generateRecuEcheance}
          downloadPartialReceipt={downloadPartialReceipt}
          openEditAcompteModal={openEditAcompteModal}
          cancelAcompte={cancelAcompte}
          generatingRecu={generatingRecu}
          archiving={archiving}
          archiveRecuToGED={archiveRecuToGED}
          filterRecuStatus={filterRecuStatus}
          setFilterRecuStatus={setFilterRecuStatus}
          filterDateDebut={filterDateDebut}
          setFilterDateDebut={setFilterDateDebut}
          filterDateFin={filterDateFin}
          setFilterDateFin={setFilterDateFin}
          exportAcomptes={exportAcomptes}
          calculatedComptaStats={calculatedComptaStats}
        />
      )}

      {/* === GENERER UN RECU MANUELLEMENT === */}
      {activeTab === 'generer-recu' && (
        <GenerationRecuManuel />
      )}

      {/* === ONGLET SEPA RCD SOLIDAIRE (V35.35) === */}
      {activeTab === 'sepa-rcd' && (
        <SepaCbPanel />
      )}

      {activeTab === 'domium-paiements' && <PaiementsDomiumESS />}

      {activeTab === 'recus-mandats' && <RecusMandatsSEPA />}

      {activeTab === 'rejets-mandats' && <RejetsMandatsSEPA />}

      {activeTab === 'taux-encaissement' && <TauxEncaissement />}

      {activeTab === 'a-reclamer' && <PaiementsAReclamer />}
      {activeTab === 'impayes' && <TableauImpayes />}

      {/* Résultats pour onglets attente/validés */}
      {(activeTab === 'attente' || activeTab === 'valides') && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            {filteredDocuments.length} résultat(s) 
            {filterType !== 'all' && ` • Filtre: ${TYPE_CONFIG[filterType]?.label}`}
          </span>
        </div>
      )}

      {/* Barre d'actions en masse */}
      {activeTab === 'attente' && filteredDocuments.length > 0 && (
        <Card className="border-2 border-slate-200 bg-slate-50">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={isAllFilteredSelected ? deselectAll : selectAllFiltered}
                  className="flex items-center gap-2"
                >
                  {isAllFilteredSelected ? (
                    <>
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                      Tout désélectionner
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4" />
                      Tout sélectionner
                    </>
                  )}
                </Button>
                
                {selectedIds.size > 0 && (
                  <Badge className="bg-blue-700 text-white text-sm px-3 py-1 font-semibold">
                    {selectedIds.size} sélectionné(s)
                  </Badge>
                )}
              </div>

              {selectedIds.size > 0 && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleBulkValidate}
                    disabled={bulkProcessing}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                  >
                    {bulkProcessing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCheck className="h-4 w-4 mr-2" />
                    )}
                    Valider ({selectedIds.size})
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={bulkProcessing}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {bulkProcessing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Supprimer ({selectedIds.size})
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grille des documents - uniquement pour onglets attente et valides */}
      {(activeTab === 'attente' || activeTab === 'valides') && (
        <>
          {filteredDocuments.length === 0 ? (
            <Card className="border-2 border-dashed border-gray-300">
              <CardContent className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucun document trouvé</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {searchTerm || filterType !== 'all' 
                    ? 'Modifiez vos critères de recherche pour afficher plus de résultats.'
                    : activeTab === 'attente'
                      ? 'Aucune facture en attente de validation pour le moment.'
                      : 'Aucun paiement validé pour le moment.'}
                </p>
                {(searchTerm || filterType !== 'all') && (
                  <Button variant="outline" onClick={resetFilters} className="mt-4">
                    Réinitialiser les filtres
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredDocuments.map(doc => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  calculateMontantTTC={calculateMontantTTC}
                  calculateMontantHT={calculateMontantHT}
                  selectedIds={selectedIds}
                  toggleSelection={toggleSelection}
                  openValidationModal={openValidationModal}
                  openPartialPaymentModal={openPartialPaymentModal}
                  openEcheanceModal={openEcheanceModal}
                  openEcheancierModal={openEcheancierModal}
                  openStripeSepaModal={openStripeSepaModal}
                  openShareModal={openShareModal}
                  downloadRecu={downloadRecu}
                  viewRecu={viewRecu}
                  printEcheancier={printEcheancier}
                  handleDeleteEcheancier={handleDeleteEcheancier}
                  handleResetEcheancier={handleResetEcheancier}
                  initiateStripeCBPayment={initiateStripeCBPayment}
                  generatingRecu={generatingRecu}
                  scheduleSepaDebit={scheduleSepaDebit}
                  activeTab={activeTab}
                  setSelectedDocument={setSelectedDocument}
                  setAssignForm={setAssignForm}
                  setShowAssignModal={setShowAssignModal}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* All Modals */}
      <Modals
        showValidationModal={showValidationModal}
        setShowValidationModal={setShowValidationModal}
        selectedDocument={selectedDocument}
        validationData={validationData}
        setValidationData={setValidationData}
        validating={validating}
        handleValidatePayment={handleValidatePayment}
        calculateMontantTTC={calculateMontantTTC}
        calculateMontantHT={calculateMontantHT}
        showAssignModal={showAssignModal}
        setShowAssignModal={setShowAssignModal}
        assignForm={assignForm}
        setAssignForm={setAssignForm}
        handleAssign={handleAssign}
        coopers={coopers}
        members={members}
        experts={experts}
        showShareModal={showShareModal}
        setShowShareModal={setShowShareModal}
        shareForm={shareForm}
        setShareForm={setShareForm}
        sharing={sharing}
        handleShare={handleShare}
        showPartialPaymentModal={showPartialPaymentModal}
        setShowPartialPaymentModal={setShowPartialPaymentModal}
        partialPaymentData={partialPaymentData}
        setPartialPaymentData={setPartialPaymentData}
        partialPaymentsHistory={partialPaymentsHistory}
        loadingPartialPayments={loadingPartialPayments}
        processingPartialPayment={processingPartialPayment}
        handlePartialPayment={handlePartialPayment}
        downloadPartialReceipt={downloadPartialReceipt}
        archiveRecuToGED={archiveRecuToGED}
        openRecuEcheanceModal={openRecuEcheanceModal}
        generateRecuEcheance={generateRecuEcheance}
        generatingRecu={generatingRecu}
        showEcheanceModal={showEcheanceModal}
        setShowEcheanceModal={setShowEcheanceModal}
        echeanceForm={echeanceForm}
        setEcheanceForm={setEcheanceForm}
        updatingEcheance={updatingEcheance}
        handleUpdateEcheance={handleUpdateEcheance}
        showEcheancierModal={showEcheancierModal}
        setShowEcheancierModal={setShowEcheancierModal}
        echeancierForm={echeancierForm}
        setEcheancierForm={setEcheancierForm}
        creatingEcheancier={creatingEcheancier}
        handleCreateEcheancier={handleCreateEcheancier}
        addEcheanceToForm={addEcheanceToForm}
        removeEcheanceFromForm={removeEcheanceFromForm}
        showStripeSepaModal={showStripeSepaModal}
        setShowStripeSepaModal={setShowStripeSepaModal}
        stripeSepaType={stripeSepaType}
        setStripeSepaType={setStripeSepaType}
        stripeProcessing={stripeProcessing}
        sepaModalEmail={sepaModalEmail}
        setSepaModalEmail={setSepaModalEmail}
        sepaModalName={sepaModalName}
        setSepaModalName={setSepaModalName}
        setupStripeSEPAWithEmail={setupStripeSEPAWithEmail}
        showEditAcompteModal={showEditAcompteModal}
        setShowEditAcompteModal={setShowEditAcompteModal}
        acompteForm={acompteForm}
        setAcompteForm={setAcompteForm}
        saveAcompteModification={saveAcompteModification}
        getAuthHeader={getAuthHeader}
        initiateStripeCBPayment={initiateStripeCBPayment}
        openStripeSepaModal={openStripeSepaModal}
        onPaiementAnnule={async () => { await loadPartialPaymentsHistory(selectedDocument.id); fetchData(); }}
      />
    </div>
  );
}

